// Simple encryption/decryption for token storage
// Uses base64 encoding with a simple XOR cipher for basic obfuscation
// Note: This is not cryptographically secure, but adds a layer of obfuscation

const USER_TOKEN_KEY = 'token';
const ADMIN_TOKEN_KEY = 'adminToken';
const ENCRYPTION_KEY = 'simpler-home-token-key'; // Simple key for XOR

// JWT token expiry checking
const parseJwt = (token: string): any => {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );
        return JSON.parse(jsonPayload);
    } catch {
        return null;
    }
};

export const isTokenExpired = (token: string | null): boolean => {
    if (!token) return true;

    const payload = parseJwt(token);
    if (!payload || !payload.exp) return true;

    // exp is in seconds, Date.now() is in milliseconds
    const expiryTime = payload.exp * 1000;
    const currentTime = Date.now();

    // Token is expired if current time is past expiry time
    return currentTime >= expiryTime;
};

export const getTokenExpiryTime = (token: string | null): number | null => {
    if (!token) return null;

    const payload = parseJwt(token);
    if (!payload || !payload.exp) return null;

    return payload.exp * 1000; // Convert to milliseconds
};

const xorEncrypt = (text: string, key: string): string => {
    let result = '';
    for (let i = 0; i < text.length; i++) {
        result += String.fromCharCode(text.charCodeAt(i) ^ key.charCodeAt(i % key.length));
    }
    return result;
};

const toBase64 = (str: string): string => {
    try {
        return btoa(encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (_, p1) => String.fromCharCode(parseInt(p1, 16))));
    } catch {
        return '';
    }
};

const fromBase64 = (str: string): string => {
    try {
        return decodeURIComponent(
            atob(str)
                .split('')
                .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );
    } catch {
        return '';
    }
};

// User token functions
export const saveToken = (token: string | undefined | null): void => {
    if (!token) {
        console.warn('Attempted to save undefined/null token');
        return;
    }
    try {
        const encrypted = toBase64(xorEncrypt(token, ENCRYPTION_KEY));
        localStorage.setItem(USER_TOKEN_KEY, encrypted);
    } catch (error) {
        console.error('Error saving token:', error);
    }
};

export const getToken = (): string | null => {
    try {
        const encrypted = localStorage.getItem(USER_TOKEN_KEY);
        if (!encrypted) return null;
        const decrypted = xorEncrypt(fromBase64(encrypted), ENCRYPTION_KEY);
        return decrypted || null;
    } catch (error) {
        console.error('Error getting token:', error);
        return null;
    }
};

export const removeToken = (): void => {
    try {
        localStorage.removeItem(USER_TOKEN_KEY);
    } catch (error) {
        console.error('Error removing token:', error);
    }
};

export const hasToken = (): boolean => {
    return !!getToken();
};

// Admin token functions
export const saveAdminToken = (token: string | undefined | null): void => {
    if (!token) {
        console.warn('Attempted to save undefined/null admin token');
        return;
    }
    try {
        const encrypted = toBase64(xorEncrypt(token, ENCRYPTION_KEY));
        localStorage.setItem(ADMIN_TOKEN_KEY, encrypted);
    } catch (error) {
        console.error('Error saving admin token:', error);
    }
};

export const getAdminToken = (): string | null => {
    try {
        const encrypted = localStorage.getItem(ADMIN_TOKEN_KEY);
        if (!encrypted) return null;
        const decrypted = xorEncrypt(fromBase64(encrypted), ENCRYPTION_KEY);
        return decrypted || null;
    } catch (error) {
        console.error('Error getting admin token:', error);
        return null;
    }
};

export const removeAdminToken = (): void => {
    try {
        localStorage.removeItem(ADMIN_TOKEN_KEY);
    } catch (error) {
        console.error('Error removing admin token:', error);
    }
};
